import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Users, Info } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../../lib/apiError';

const MAX_TIERS = 20;

const TeacherPricing = () => {
  const { profile, reload } = useOutletContext();
  const [rates, setRates] = useState(profile.pricing_tiers.map((t) => String(t.rate_per_hour)));
  const [saving, setSaving] = useState(false);

  const setRate = (i, v) => setRates((r) => r.map((x, j) => (j === i ? v : x)));
  const valid = rates.every((r) => Number(r) > 0);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/teachers/${profile.id}/pricing`, {
        tiers: rates.map((r, i) => ({ students: i + 1, rate_per_hour: Number(r) })),
      });
      toast.success('Group pricing saved');
      reload();
    } catch (e) {
      toast.error(apiError(e, 'Could not save pricing'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="teacher-pricing-page">
      <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">Group Pricing</h1>
      <p className="text-base md:text-lg text-slate-600 mb-8">Set your hourly rate for each group size. Your largest group size is the most students a session can have.</p>

      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-violet-600" />Rate per hour</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {rates.map((r, i) => (
            <div key={`tier-${i + 1}`} className="flex items-center gap-3" data-testid={`pricing-row-${i + 1}`}>
              <span className="w-28 text-sm font-medium text-slate-700">{i === 0 ? '1 student' : `${i + 1} students`}</span>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <Input type="number" min="1" className="pl-7" value={r} onChange={(e) => setRate(i, e.target.value)} data-testid={`pricing-rate-input-${i + 1}`} />
              </div>
              <span className="text-xs text-slate-400 w-20">/hour total</span>
              {i === rates.length - 1 && i > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => setRates(rates.slice(0, -1))} className="text-rose-500" data-testid="pricing-remove-tier" aria-label="Remove largest group size">
                  <Trash2 className="w-4 h-4" />
                </Button>
              ) : <span className="w-9" />}
            </div>
          ))}

          <div className="flex flex-wrap gap-3 pt-3">
            <Button variant="outline" className="rounded-full" disabled={rates.length >= MAX_TIERS}
              onClick={() => setRates([...rates, rates[rates.length - 1] || ''])} data-testid="pricing-add-tier">
              <Plus className="w-4 h-4 mr-2" />Add {rates.length + 1} students
            </Button>
            <Button onClick={save} disabled={!valid || saving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="pricing-save-button">
              {saving ? 'Saving...' : 'Save Pricing'}
            </Button>
          </div>

          <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 mt-4">
            <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-600">
              The booking student pays the full rate for the group size they choose. New prices apply to new bookings only —
              sessions already paid for keep their locked price.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeacherPricing;
